/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION (DRAFT — W-23789028 consent rework): the SINGLE source of truth for whether
 * the TrueVault CMP is effectively enabled.
 *
 * Both `config.server.ts` (to disable the built-in binary `TrackingConsentBanner`) and
 * `truevault-consent.tsx` (to decide whether to load Polaris) MUST use this exact predicate, so the
 * two can never diverge into "neither banner renders." Env-gating on the scriptUrl is the ONLY switch
 * — set the env var on the MRT environment(s) that should run TrueVault. (No host-allowlist: a
 * client-runtime host guard cannot be mirrored by the server-evaluated banner-disable flag, and the
 * divergence would strand hosts with no consent UI at all.)
 */

/**
 * The only host TrueVault serves the Polaris loader from
 * (`https://polaris.truevaultcdn.com/static/pc/<PrivacyCenterID>/polaris.js`). We ORIGIN-PIN to it so
 * a misconfigured/injected env var cannot point the loader at an arbitrary origin. This code-level
 * pin — not the CSP allowlist — is the real guard: the app's CSP is nonce-based WITH `strict-dynamic`,
 * and the loader is injected carrying a valid nonce, so CSP permits it regardless of origin (a
 * `'nonce-…'` source is host-agnostic, and under `strict-dynamic` the `script-src` host allowlist is
 * ignored for nonced scripts). The `script-src` `polaris.truevaultcdn.com` entry stays as
 * defense-in-depth for user agents that don't honor `strict-dynamic`.
 */
const TRUEVAULT_LOADER_HOST = 'polaris.truevaultcdn.com';

/**
 * Validate + normalize a TrueVault polaris.js URL. Returns the normalized https href, or `null` if
 * the value is missing, malformed, not https, or not served from the pinned TrueVault CDN host.
 * Using the parsed `href` (not the raw string) for interpolation also neutralizes `</script>`-style
 * breakout, since URL parsing percent-encodes `<`/`>` in the path/query.
 */
export function resolveTrueVaultScriptUrl(raw?: string): string | null {
    const trimmed = raw?.trim();
    if (!trimmed) return null;
    let parsed: URL;
    try {
        parsed = new URL(trimmed);
    } catch {
        return null;
    }
    if (parsed.protocol !== 'https:') return null;
    if (parsed.hostname !== TRUEVAULT_LOADER_HOST) return null;
    return parsed.href;
}

/** True when a valid https TrueVault scriptUrl is configured (CMP is on). */
export function isTrueVaultEnabled(raw?: string): boolean {
    return resolveTrueVaultScriptUrl(raw) !== null;
}

/**
 * A TrueVault Privacy Center is served EITHER from a customer subdomain of this suffix
 * (`https://<id>.truevaultprivacycenter.com`) OR from a customer VANITY domain that TrueVault CNAMEs to
 * `<PrivacyCenterID>.perm.truevaultprivacycenter.com` (e.g. Baggallini's `privacy.baggallini.com`
 * → `lw7t6233n.perm.truevaultprivacycenter.com`). We accept the suffix PLUS an explicit allowlist of
 * those vanity hosts, so a misconfigured/injected env var still can't point the compliance links
 * (Privacy Policy, CA Privacy Notice, "Your Privacy Choices" opt-out) at an arbitrary origin. This host
 * is NOT derivable from the polaris.js `scriptUrl` (that lives on polaris.truevaultcdn.com), so it is
 * its own env var.
 */
const TRUEVAULT_PRIVACY_CENTER_SUFFIX = '.truevaultprivacycenter.com';

/**
 * Customer vanity domains TrueVault serves the Privacy Center from (CNAME'd to
 * `<PrivacyCenterID>.perm.truevaultprivacycenter.com`). Exact-host allowlist — add a customer's branded
 * privacy host here when they use one instead of the default `*.truevaultprivacycenter.com` subdomain.
 */
const TRUEVAULT_PRIVACY_CENTER_ALLOWED_HOSTS = ['privacy.baggallini.com'];

/**
 * Validate + normalize the Privacy Center base URL. Returns the bare https origin with no trailing
 * slash (e.g. `https://privacy.baggallini.com`), or `null` if missing, malformed, not https, or not on
 * the pinned `*.truevaultprivacycenter.com` host / an allowlisted vanity host.
 */
export function resolveTrueVaultPrivacyCenterUrl(raw?: string): string | null {
    const trimmed = raw?.trim();
    if (!trimmed) return null;
    let parsed: URL;
    try {
        parsed = new URL(trimmed);
    } catch {
        return null;
    }
    if (parsed.protocol !== 'https:') return null;
    const { hostname } = parsed;
    const hostAllowed =
        hostname.endsWith(TRUEVAULT_PRIVACY_CENTER_SUFFIX) || TRUEVAULT_PRIVACY_CENTER_ALLOWED_HOSTS.includes(hostname);
    if (!hostAllowed) return null;
    return parsed.origin;
}

/**
 * The visible-notice link set (Privacy Policy, California Privacy Notice, Notice of Financial
 * Incentive, and the "Your Privacy Choices" opt-out + its icon). This is the SINGLE source of truth
 * the footer and every point-of-collection notice read: it returns a link set ONLY when BOTH the CMP
 * loader (`scriptUrl`) AND the Privacy Center base (`privacyCenterUrl`) are validly configured — so
 * the visible links flip on with the exact same switch that loads Polaris (which is what reveals the
 * `hidden` per-state links) and disables the built-in banner. Returns `null` otherwise, and callers
 * fall back to the pre-TrueVault internal links, leaving no compliance gap during rollout.
 */
export interface TrueVaultPrivacyLinks {
    privacyPolicy: string;
    californiaPrivacyNotice: string;
    financialIncentive: string;
    optOut: string;
    optOutIconUrl: string;
}

export function getTrueVaultPrivacyLinks(config?: {
    scriptUrl?: string;
    privacyCenterUrl?: string;
}): TrueVaultPrivacyLinks | null {
    const scriptUrl = resolveTrueVaultScriptUrl(config?.scriptUrl);
    const base = resolveTrueVaultPrivacyCenterUrl(config?.privacyCenterUrl);
    if (!scriptUrl || !base) return null;
    // The opt-out icon is published on the same origin as the pinned polaris.js loader.
    const optOutIconUrl = `${new URL(scriptUrl).origin}/static/assets/icons/optout-icon-blue.svg`;
    return {
        privacyPolicy: `${base}/privacy-policy`,
        californiaPrivacyNotice: `${base}/privacy-policy#california-privacy-notice`,
        financialIncentive: `${base}/privacy-policy#financial-incentive`,
        optOut: `${base}/opt-out`,
        optOutIconUrl,
    };
}
